import React, { useEffect, useMemo, useState } from "react";
import { getStockSnapshot } from "../marketData/marketDataApi";
import LiveProfessionalChartWorkspace from "./LiveProfessionalChartWorkspace";
import "./TradingModeChartWorkspace.css";

function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function snapshotPrice(snapshot) {
  return (
    finiteNumber(snapshot?.latest_trade?.p) ??
    finiteNumber(snapshot?.minute_bar?.c) ??
    finiteNumber(snapshot?.daily_bar?.c)
  );
}

function normalizedTargets(targets = {}) {
  return {
    target1: finiteNumber(targets.target1),
    target2: finiteNumber(targets.target2),
    target3: finiteNumber(targets.target3),
  };
}

/**
 * Canonical adapter for trading-mode chart surfaces.
 *
 * Live/Paper/Paper Auto may have different execution permissions, but they
 * render the same professional market chart. This adapter owns authenticated
 * snapshot loading for non-Live hosts and never substitutes simulated prices
 * when authenticated market data is unavailable.
 */
function TradingModeChartWorkspace({
  symbol,
  modeLabel,
  executionLabel,
  entry = null,
  stop = null,
  targets = {},
  direction = "Long",
  market = null,
  onSnapshotPrice = null,
  children = null,
}) {
  const cleanSymbol = String(symbol || "").trim().toUpperCase() || "AAPL";
  const [snapshotState, setSnapshotState] = useState({
    status: "loading",
    data: null,
    error: "",
  });

  useEffect(() => {
    let active = true;
    setSnapshotState({ status: "loading", data: null, error: "" });

    getStockSnapshot(cleanSymbol)
      .then((data) => {
        if (!active) return;
        setSnapshotState({ status: "ready", data, error: "" });
        const price = snapshotPrice(data);
        if (price !== null && typeof onSnapshotPrice === "function") {
          onSnapshotPrice(price);
        }
      })
      .catch((error) => {
        if (!active) return;
        setSnapshotState({
          status: "error",
          data: null,
          error:
            error?.message ||
            "Authenticated market data is unavailable. No simulated chart price was substituted.",
        });
      });

    return () => {
      active = false;
    };
  }, [cleanSymbol, onSnapshotPrice]);

  const currentPrice = snapshotPrice(snapshotState.data);
  const trade = useMemo(
    () => ({
      symbol: cleanSymbol,
      direction,
      currentPrice,
      entry: finiteNumber(entry),
      stop: finiteNumber(stop),
      targets: normalizedTargets(targets),
    }),
    [cleanSymbol, currentPrice, direction, entry, stop, targets],
  );

  const marketContext = market || {
    mood: "Market data connected",
    bias: "Neutral",
    confidence: 0,
    riskLevel: "Unrated",
  };

  return (
    <section
      className="tradingModeChartWorkspace"
      data-chart-engine="anina-professional-shared"
      data-trading-mode={String(modeLabel || "UNKNOWN")
        .toLowerCase()
        .replace(/\s+/g, "-")}
    >
      <div className="tradingModeChartStatus" role="status">
        <div>
          <span className="tradingModeChartMode">{modeLabel}</span>
          <strong>{executionLabel}</strong>
        </div>
        <div>
          <span>MARKET DATA</span>
          <strong>
            {snapshotState.status === "ready"
              ? `${snapshotState.data?.source?.provider || "Alpaca"} ${String(
                  snapshotState.data?.source?.feed || "authenticated",
                ).toUpperCase()}`
              : snapshotState.status === "loading"
              ? "AUTHENTICATED DATA LOADING"
              : "AUTHENTICATED DATA UNAVAILABLE"}
          </strong>
        </div>
      </div>

      {snapshotState.status === "loading" && (
        <div className="tradingModeChartLoading" aria-live="polite">
          Loading authenticated market chart for {cleanSymbol}…
        </div>
      )}

      {snapshotState.status === "error" && (
        <div className="tradingModeChartError" role="alert">
          <strong>Chart data unavailable — fail closed</strong>
          <span>{snapshotState.error}</span>
        </div>
      )}

      {children}

      {snapshotState.status === "ready" && (
        <LiveProfessionalChartWorkspace
          trade={trade}
          market={marketContext}
          snapshot={snapshotState.data}
          dataStatus="ready"
          isDemoActive={false}
        />
      )}
    </section>
  );
}

export { snapshotPrice };
export default TradingModeChartWorkspace;
